import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { STOCK_ADJUSTMENT_ACTION, STOCK_ADJUSTMENT_TYPE } from 'khufu-legos';
import { CloseButton, Col, Form, Modal as ModalComponent, Row } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { Option } from 'react-bootstrap-typeahead/types/types';
import { useForm } from 'react-hook-form';

import { createStockAdjustment, getProducts } from '../../apis/queries';
import SubmitButton from '../../components/Buttons/SubmitButton';
import DisplayFieldErrors from '../../utils/DisplayFormError';
import { notification, NotificationKinds } from '../../widgets/Notifications/notifications';
import { PRODUCTS_QUERY_KEY } from '../Products/Products';
import { STOCK_ADJUSTMENTS_QUERY_KEY } from './StockAdjustment';

interface CreateStockAdjustmentModalProps {
  show: boolean;
  handleClose: () => void;
}

interface Product {
  _id: string;
  name: string;
}

interface CreateStockAdjustmentFormInput {
  productId: string;
  type: STOCK_ADJUSTMENT_TYPE;
  action: STOCK_ADJUSTMENT_ACTION;
  quantity: number;
}

const CreateStockAdjustmentModal = ({ show, handleClose }: CreateStockAdjustmentModalProps) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (stockAdjustment: CreateStockAdjustmentFormInput) => {
      return createStockAdjustment(stockAdjustment);
    },
    onSuccess: data => {
      const { data: newStockAdjustment } = data;
      notification({
        variant: NotificationKinds.SUCCESS,
        title: `STOCK ADJUSTMENT CREATED`,
        subtitle: `Stock adjustment for ${newStockAdjustment?.product?.name} has been added`
      });

      handleClose();
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: STOCK_ADJUSTMENTS_QUERY_KEY });
    }
  });

  const loading = mutation?.isPending;

  const methods = useForm<CreateStockAdjustmentFormInput>({
    mode: 'onTouched',
    defaultValues: {
      productId: '',
      type: STOCK_ADJUSTMENT_TYPE.DEPLETING,
      action: STOCK_ADJUSTMENT_ACTION.BREAKAGES_DAMAGES,
      quantity: 0
    }
  });

  const {
    getValues,
    register,
    formState: { isValid, errors },
    setValue,
    watch
  } = methods;

  const onCreateStockAdjustment = () => {
    const fields = getValues();
    mutation.mutate(fields);
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedType = e.target.value as STOCK_ADJUSTMENT_TYPE;
    setValue('type', selectedType);

    const actionValue =
      selectedType === STOCK_ADJUSTMENT_TYPE.DEPLETING
        ? STOCK_ADJUSTMENT_ACTION.BREAKAGES_DAMAGES
        : STOCK_ADJUSTMENT_ACTION.EXCESSES;
    setValue('action', actionValue);
  };

  const selectProduct = (option: Option[]) => {
    if (option.length) {
      const selectedProduct = option[0] as Product;
      setValue('productId', selectedProduct._id);
    }
  };

  return (
    <ModalComponent show={show} onHide={handleClose} centered>
      <ModalComponent.Header>
        <h4 className="card-header-title">New Stock Adjustment</h4>
        <CloseButton onClick={handleClose} />
      </ModalComponent.Header>
      <ModalComponent.Body className="overflow-auto d-flex flex-column flex-grow-1">
        <Form>
          <div className="form-group">
            <Form.Label htmlFor="product">Product</Form.Label>
            <ProductTypeahead selectProduct={selectProduct} />
            {errors?.productId && <DisplayFieldErrors displayText={errors.productId.message!} />}
          </div>

          <div className="form-group">
            <Form.Label htmlFor="type">Type</Form.Label>
            <Form.Select id="type" {...register('type', { required: true })} onChange={handleTypeChange}>
              <option value="depleting">Depleting</option>
              <option value="additive">Additive</option>
            </Form.Select>
            {errors?.type && <DisplayFieldErrors displayText={errors.type.message!} />}
          </div>

          <div className="form-group">
            <Form.Label htmlFor="action">Action</Form.Label>
            <Form.Select id="action" {...register('action', { required: true })}>
              {watch('type') === STOCK_ADJUSTMENT_TYPE.DEPLETING && (
                <>
                  <option value={STOCK_ADJUSTMENT_ACTION.BREAKAGES_DAMAGES}>Damages</option>
                  <option value={STOCK_ADJUSTMENT_ACTION.MISSING_PRODUCTS}>Missing Products</option>
                  <option value={STOCK_ADJUSTMENT_ACTION.BAD_PRODUCTS}>Bad Products</option>
                </>
              )}
              {watch('type') === STOCK_ADJUSTMENT_TYPE.ADDITIVE && (
                <>
                  <option value={STOCK_ADJUSTMENT_ACTION.EXCESSES}>Excesses</option>
                  <option value={STOCK_ADJUSTMENT_ACTION.NBC_ADDITIONS}>NBC Additions</option>
                </>
              )}
            </Form.Select>
            {errors?.action && <DisplayFieldErrors displayText={errors.action.message!} />}
          </div>

          <div className="form-group">
            <Form.Label htmlFor="quantity">Quantity</Form.Label>
            <Form.Control
              type="number"
              id="quantity"
              {...register('quantity', { required: true, min: 1, valueAsNumber: true })}
            />
            {errors?.quantity && <DisplayFieldErrors displayText={errors.quantity.message!} />}
          </div>

          <SubmitButton
            buttonText="Create Stock Adjustment"
            loading={loading}
            onClick={onCreateStockAdjustment}
            disabled={!isValid || loading}
          />
        </Form>
      </ModalComponent.Body>
    </ModalComponent>
  );
};

interface ProductTypeaheadProps {
  selectProduct: (option: Option[]) => void;
}

const ProductTypeahead = ({ selectProduct }: ProductTypeaheadProps) => {
  const compositeProductsQueryKey = [PRODUCTS_QUERY_KEY];

  const { data: productsData, isLoading: isLoadingProducts } = useQuery({
    queryKey: compositeProductsQueryKey,
    queryFn: () => getProducts()
  });

  return (
    <Row>
      <Col>
        <Typeahead
          id={`product-search`}
          onChange={selection => selectProduct(selection)}
          disabled={isLoadingProducts}
          placeholder={`Select a product...`}
          options={productsData?.results ?? []}
          labelKey="name"
          minLength={2}
        />
      </Col>
    </Row>
  );
};

export default CreateStockAdjustmentModal;
